import { ChevronDown, ChevronLeft, ChevronRight, Import, Plus, SearchLoop } from 'assets/icons';
import StyledInput from 'components/styledInput/StyledInput';
import Button from 'components/button';
import UseContext from 'hooks/useContext';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import removeAccents from 'remove-accents';
import Fuse from 'fuse.js';
import ApiDatabase from 'server';
import Usefetchdata from 'hooks/usefetchdata';
import PageForm from '../../components/pageForm/PageForm';
import BillingSubNav from '../../components/subnav/BillingSubNav';
import SubNavbar from '../../components/SubNavbar/SubNavbar';
import { toast } from 'react-toastify';
import InvoiceImportErrorBanner from 'components/invoiceImport/InvoiceImportErrorBanner';
import InvoiceImportToast from 'components/invoiceImport/InvoiceImportToast';
import useInvoiceImportRun from 'components/invoiceImport/useInvoiceImportRun';
import {
  INFORMATION_SAVING_CODE,
  INFORMATION_SAVING_TEXT,
  INVOICE_IMPORT_STARTED,
  INVOICE_LIST_STALE,
  monthLabel,
} from 'components/invoiceImport/invoiceImportText';
import useMonthInvoices, { compareMonths, getLastInvoiceMonth } from 'components/invoiceList/useMonthInvoices';
import InvoiceFilters from 'components/invoiceList/InvoiceFilters';
import useInvoiceFilters from 'components/invoiceList/useInvoiceFilters';
import useInvoiceSelection from 'components/invoiceList/useInvoiceSelection';
import useInvoiceDisplayMode from 'components/invoiceList/useInvoiceDisplayMode';
import InvoiceImportJournal from 'components/invoiceImportJournal/InvoiceImportJournal';
import InvoiceHeaderMenu from 'components/invoiceImportJournal/InvoiceHeaderMenu';
import { INVOICE_DETAIL_SLIDE_OVER } from 'components/invoiceDetail/useOpenInvoiceDetail';
import { INVOICE_MAIL_SENT } from 'components/invoiceSendMail/invoiceSendMailText';
import useSlideOver from 'components/SlideOver/useSlideOver';
import RemindersSearch from 'components/invoiceReminders/RemindersSearch';
import useRemindersSummary from 'components/invoiceReminders/useRemindersSummary';
import useReminderRunTracking from 'components/invoiceReminders/useReminderRunTracking';
import { invoiceRemindersText } from 'components/invoiceReminders/invoiceRemindersText';

// The month the URL asks for (?y=2025&m=04), if it is a readable one.
const getMonthOfUrl = (searchParams) => {
  const y = searchParams.get('y');
  const m = searchParams.get('m');
  if (y && m) {
    const d = new Date(Number(y), Number(m) - 1, 1);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
};

/**
 * The layout of the two pages of the sidebar, « Paie » (`section` "payroll":
 * the payslips, and the history and stats reached by their address only) and
 * « Facturation » (`section` "billing": the client invoices and the
 * reminders). Each asks for its own access; the tab of the URL decides the
 * controls of the header.
 */
const PayslipManagement = ({ section }) => {
  const { userData } = Usefetchdata();
  const isBilling = section === 'billing';
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setModalData, setModalShow, setModalType, refreshPayslips, modalDataReturn, language, slideOverType, removeAllParameters } = UseContext();
  const { toggleSlideOver } = useSlideOver();
  const languageText = (key, fallback) => language.getValue(`invoiceImport.${key}`, fallback);
  const invoiceListText = (key, fallback) => language.getValue(`invoiceList.${key}`, fallback);

  const openModal = (type) => {
    setModalType(type);
    setModalShow(true);
  };

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage] = useState(20);

  const isPayslipTab = location.pathname.includes('payslips');
  const isInvoiceTab = location.pathname.includes('/invoices');
  const isRemindersTab = location.pathname.includes('/reminders');
  const isHistory = location.pathname.includes('history') || location.pathname.includes('stats');
  const showControls = isPayslipTab || isInvoiceTab;

  // The month of the page lives in the URL (?y=2025&m=04) and nowhere else, so the
  // page and the URL cannot disagree. Without one (the tabs of the sub navigation
  // drop the query string) it is the month shown last.
  const lastMonthShown = useRef(null);
  const selectedDate = getMonthOfUrl(searchParams) ?? lastMonthShown.current ?? new Date();

  // The api lists no invoice after the current month, counted in UTC (JBWEB-1269):
  // on the invoice tab a later month is brought back to it, and « > » stops there.
  const lastInvoiceMonth = getLastInvoiceMonth();
  // The picker compares days: its limit is the last day of that month, so that no day of it is out of bounds.
  const lastInvoiceDay = new Date(lastInvoiceMonth.getFullYear(), lastInvoiceMonth.getMonth() + 1, 0);
  const monthVersusLast = compareMonths(selectedDate, lastInvoiceMonth);
  const isAfterLastInvoiceMonth = isInvoiceTab && monthVersusLast > 0;
  const isAtLastInvoiceMonth = isInvoiceTab && monthVersusLast >= 0;
  const displayedDate = isAfterLastInvoiceMonth ? lastInvoiceMonth : selectedDate;

  const year = String(displayedDate.getFullYear());
  const month = String(displayedDate.getMonth() + 1).padStart(2, '0');

  // ── URL sync ──────────────────────────────────────────────────────
  // Replaced, not pushed: choosing months leaves no history entry behind.
  const putMonthInUrl = (date) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('y', String(date.getFullYear()));
      next.set('m', String(date.getMonth() + 1).padStart(2, '0'));
      return next;
    }, { replace: true });
  };

  // Same name and shape as the state it replaces, so the arrows and the picker are
  // unchanged: they choose a month by writing it in the URL. A cleared picker (null)
  // means the current month.
  const setSelectedDate = (next) => {
    putMonthInUrl((typeof next === 'function' ? next(displayedDate) : next) ?? new Date());
  };

  // The URL says the month shown: a month later than the api lists, a missing or
  // unreadable one, or a loose one (m=4) is written back.
  useEffect(() => {
    if (!showControls) return;
    if (searchParams.get('y') === year && searchParams.get('m') === month) return;
    putMonthInUrl(displayedDate);
  }, [year, month, showControls, searchParams]);

  // What an address without a month falls back on.
  useEffect(() => {
    lastMonthShown.current = displayedDate;
  }, [year, month]);

  // ── Payslip state ─────────────────────────────────────────────────
  const [payslips, setPayslips] = useState([]);
  const [payslipTotalAmount, setPayslipTotalAmount] = useState(0);
  const [isWaitingPayslip, setIsWaitingPayslip] = useState(true);
  const [filteredPayslips, setFilteredPayslips] = useState([]);

  const fetchPayslips = () => {
    setIsWaitingPayslip(true);
    ApiDatabase.listPayslips(
      { year, month },
      (data) => {
        setPayslips(data.payslips || []);
        setPayslipTotalAmount(data.totalAmount || 0);
        setIsWaitingPayslip(false);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.log(err);
        setIsWaitingPayslip(false);
      }
    );
  };

  useEffect(() => {
    if (!isPayslipTab) return;
    fetchPayslips();
    setCurrentPage(1);
    setSelectedIds([]);
  }, [year, month, refreshPayslips, isPayslipTab]);

  useEffect(() => {
    if (!isPayslipTab) return;
    let result = payslips;
    if (statusFilter) result = result.filter((p) => p.status === statusFilter);
    if (search.length > 0) {
      const searchTerm = removeAccents(search.toLowerCase());
      const fuse = new Fuse(result, {
        keys: ['id_temp_worker_bestt', 'lastname', 'firstname', 'amount'],
        includeScore: true,
        threshold: 0.3,
        getFn: (obj, path) => {
          const value = Fuse.config.getFn(obj, path);
          if (value == null) return '';
          return removeAccents(value.toString().toLowerCase());
        },
      });
      result = fuse.search(searchTerm).map((r) => r.item);
    }
    setFilteredPayslips(result);
  }, [payslips, search, statusFilter, isPayslipTab]);

  // ── Invoice list (JBWEB-1269) ─────────────────────────────────────
  const period = `${year}-${month}`;
  // Search and status are read by the api (JBWEB-1270, JBWEB-1271), and kept when the month changes.
  const invoiceFilters = useInvoiceFilters(isInvoiceTab);
  // Flat, by company or by group (JBWEB-1309): the admin's choice, kept over months, filters and visits.
  const invoiceDisplay = useInvoiceDisplayMode();
  const monthInvoices = useMonthInvoices(period, isInvoiceTab, {
    ...invoiceFilters.filters,
    groupBy: invoiceDisplay.displayMode,
  });
  // Ticked invoices are kept from page to page, not to another month, other filters or another tab (JBWEB-1273).
  const invoiceSelection = useInvoiceSelection(isInvoiceTab ? monthInvoices.listKey : null, monthInvoices.list?.items);

  // ── Reminders (JBWEB-1283) ────────────────────────────────────────
  // The summary feeds the counter of the tab, whatever the tab shown: read for
  // the admins who may read invoices only.
  // Its count is shown on the reminders tab, which only billing has.
  const remindersSummary = useRemindersSummary(isBilling && Boolean(userData?.accessInvoice));
  const reminderFilters = useInvoiceFilters(isRemindersTab);
  const [remindersRefreshKey, setRemindersRefreshKey] = useState(0);
  const reminderRun = useReminderRunTracking(() => {
    setRemindersRefreshKey((key) => key + 1);
    remindersSummary.reload();
  });
  const remindersText = invoiceRemindersText(language);
  const openRemindAll = () => {
    setModalData({});
    openModal('invoice_remind_all');
  };

  // ── Invoice import (JBWEB-1266) ───────────────────────────────────
  const [importState, setImportState] = useState(null);
  const [trackedRunId, setTrackedRunId] = useState(null);
  const [isImportToastHidden, setIsImportToastHidden] = useState(false);

  // ── Import journal (JBWEB-1280) ───────────────────────────────────
  // The tab it is open on, or null when closed: closed, it reads nothing.
  const [journalTab, setJournalTab] = useState(null);
  // Counts the openings: each one mounts the journal anew, on the tab asked for, even when it is open
  // already on another tab (« Voir les erreurs » of the toast stays above it).
  const [journalOpenings, setJournalOpenings] = useState(0);
  // Changed when a run starts or ends: an open journal reads its month again.
  const [journalRefreshKey, setJournalRefreshKey] = useState(0);
  const refreshJournal = () => setJournalRefreshKey((key) => key + 1);
  const showJournal = (tab) => {
    // The journal sits under the detail panel (same z-index, and the panel comes later in the DOM): close the panel first.
    if (slideOverType === INVOICE_DETAIL_SLIDE_OVER) {
      toggleSlideOver();
      removeAllParameters?.();
    }
    setJournalTab(tab);
    setJournalOpenings((count) => count + 1);
  };
  const openJournal = () => showJournal('toFix');
  // From the header « … »: the invoices to fix if there are any, else the runs of the month (JBWEB-1281).
  // While the state of the month is not read yet, the invoices to fix: there may be some.
  const openJournalFromMenu = () =>
    showJournal(!importState || importState.unresolvedErrors > 0 ? 'toFix' : 'history');

  const followImportRun = (runId) => {
    if (!runId) return;
    setTrackedRunId(runId);
    setIsImportToastHidden(false);
    refreshJournal();
  };

  const loadImportState = () => {
    ApiDatabase.getInvoiceImportState(
      { period },
      (data) => {
        setImportState(data);
        // After a reload, a run still going is followed again.
        if (data?.runningRunId) followImportRun(data.runningRunId);
      },
      () => {},
    );
  };

  useEffect(() => {
    if (!isInvoiceTab) return;
    setImportState(null);
    loadImportState();
  }, [year, month, isInvoiceTab]);

  const importRun = useInvoiceImportRun(trackedRunId, () => {
    loadImportState();
    // The run may have fixed ticked invoices on other pages, which are not read again.
    invoiceSelection.forgetErrors();
    monthInvoices.reload();
    // A repaired invoice leaves its group of the journal.
    refreshJournal();
  });
  const isImportRunning = Boolean(trackedRunId) && importRun?.status !== 'done' && importRun?.status !== 'failed';

  // Every invoice of the month in error, or the ticked ones in error, or those of a group of the journal
  // (`fromJournal`), BeSTT then Pennylane again (JBWEB-1267).
  // Once the retry of ticked invoices runs the list is going to change: the ticks go. A group of the journal
  // leaves them, as « Tout réessayer » does.
  // One request at a time: a double click must not send two.
  const isRetryPending = useRef(false);
  const retryInvoiceImports = (invoiceNumbers, { fromJournal = false } = {}) => {
    if (isRetryPending.current) return;
    isRetryPending.current = true;
    ApiDatabase.postInvoiceImportRetry(
      invoiceNumbers ? { period, invoiceNumbers } : { period },
      (data) => {
        isRetryPending.current = false;
        followImportRun(data?.runId);
        if (invoiceNumbers && !fromJournal) invoiceSelection.clear();
      },
      (error) => {
        isRetryPending.current = false;
        if (error?.name === 'CanceledError') return;
        // Never the error object in the console: axios keeps the request, token included.
        const body = error?.response?.data;
        if (body?.errorCode === 'IMPORT_ALREADY_RUNNING' && body.runId) {
          toast.info(languageText('alreadyRunning', 'Un import de ce mois est déjà en cours : sa progression s’affiche.'));
          followImportRun(body.runId);
          return;
        }
        // An invoice of the month being saved (JBWEB-1275): no run to follow, try again in a moment.
        if (body?.errorCode === INFORMATION_SAVING_CODE) {
          toast.info(languageText(INFORMATION_SAVING_TEXT.key, INFORMATION_SAVING_TEXT.fallback));
          return;
        }
        if (body?.errorCode === 'NOTHING_TO_RETRY') {
          refreshJournal();
          if (invoiceNumbers) {
            toast.info(
              fromJournal
                ? languageText('journalNothingToRetry', 'Plus aucune de ces factures n’est en erreur.')
                : invoiceListText('selection.nothingToRetry', 'Plus aucune facture de la sélection n’est en erreur.'),
            );
            invoiceSelection.forgetErrors(invoiceNumbers);
            monthInvoices.reload();
          } else {
            toast.info(languageText('nothingToRetry', 'Plus aucune facture en erreur pour ce mois.'));
          }
          loadImportState();
          return;
        }
        // More invoices than the api takes at once (500): the whole month has no limit.
        if (body?.errorCode === 'INVOICE_NUMBERS_INVALID') {
          toast.error(languageText('tooManyInvoices', 'Trop de factures : utilisez Tout réessayer.'));
          return;
        }
        toast.error(languageText('retryFailed', 'Le réessai n’a pas pu être lancé, réessayez.'));
      },
    );
  };

  useEffect(() => {
    const socket = ApiDatabase.getSocket();
    if (!socket) return;

    const onPayslipDone = (data) => {
      if (data?.updatedPayslips?.length > 0) {
        setPayslips((prev) => prev.map((p) => {
          const upd = data.updatedPayslips.find((u) => u.id_temp_worker_bestt === p.id_temp_worker_bestt);
          if (!upd) return p;
          return { ...p, status: upd.status, amount: upd.amount };
        }));
      }
    };

    socket.on('payslipImportCompleted', onPayslipDone);
    return () => {
      socket.off('payslipImportCompleted', onPayslipDone);
    };
  }, [isInvoiceTab, isPayslipTab, year, month]);

  useEffect(() => {
    if (!modalDataReturn) return;

    if (modalDataReturn?.type === INVOICE_IMPORT_STARTED) {
      followImportRun(modalDataReturn.runId);
    } else if (modalDataReturn?.type === INVOICE_LIST_STALE || modalDataReturn?.type === INVOICE_MAIL_SENT) {
      monthInvoices.reload();
    } else if (modalDataReturn?.action === 'payslips_deleted' && modalDataReturn.id_temp_worker_bestt) {
      setPayslips((prev) => prev.map((p) =>
        modalDataReturn.id_temp_worker_bestt.includes(p.id_temp_worker_bestt)
          ? { ...p, status: 'not_imported' }
          : p
      ));
      setSelectedIds((prev) => prev.filter((id) => !modalDataReturn.id_temp_worker_bestt.includes(id)));
    } else if (modalDataReturn?.action === 'payslips_imported' && modalDataReturn.id_temp_worker_bestt) {
      setPayslips((prev) => prev.map((p) =>
        modalDataReturn.id_temp_worker_bestt.includes(p.id_temp_worker_bestt)
          ? { ...p, status: 'imported' }
          : p
      ));
    }
  }, [modalDataReturn]);

  // ── Derived data (payslips; the invoice list pages itself) ────────
  const totalAmount = payslipTotalAmount;
  const isWaiting = isWaitingPayslip;
  const countTotal = filteredPayslips.length;
  const totalPages = Math.ceil(countTotal / countPerPage);
  const itemsToDisplay = filteredPayslips.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  // Reset search/filter/selection when switching tabs
  useEffect(() => {
    setSearch('');
    setStatusFilter('');
    setSelectedIds([]);
    setCurrentPage(1);
  }, [isPayslipTab, isInvoiceTab]);

  // ── Dropdown ──────────────────────────────────────────────────────
  const [multiDropdownOpen, setMultiDropdownOpen] = useState(false);
  const [multiDropdownPos, setMultiDropdownPos] = useState({ top: 0, left: 0 });
  const multiDropdownRef = useRef(null);
  const multiDropdownMenuRef = useRef(null);

  const updateMultiDropdownPos = () => {
    if (!multiDropdownRef.current) return;
    const rect = multiDropdownRef.current.getBoundingClientRect();
    setMultiDropdownPos({
      top: rect.bottom + 4,
      left: rect.right - 224,
    });
  };

  useEffect(() => {
    const handler = (e) => {
      const inTrigger = multiDropdownRef.current?.contains(e.target);
      const inMenu = multiDropdownMenuRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) setMultiDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Guard ─────────────────────────────────────────────────────────
  const hasAccess = isBilling ? userData?.accessInvoice : userData?.accessPayslip;
  if (userData?._id && !hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  // ── Payslip actions ───────────────────────────────────────────────
  const handlePayslipImport = () => {
    setMultiDropdownOpen(false);
    const hasSelection = selectedIds.length > 0;
    const target = hasSelection
      ? payslips.filter((p) => selectedIds.includes(p.id_temp_worker_bestt))
      : payslips;
    if (target.length === 0) {
      toast.info('Aucune fiche de paie pour cette période.');
      return;
    }
    setModalData({ payslips: target, year, month, preSelected: hasSelection });
    openModal('payslip_import_global');
  };

  const handlePayslipDelete = () => {
    setMultiDropdownOpen(false);
    const target = selectedIds.length > 0
      ? payslips.filter(
          (p) => selectedIds.includes(p.id_temp_worker_bestt) && p.status === 'imported'
        )
      : payslips.filter((p) => p.status === 'imported');
    if (target.length === 0) {
      toast.info('Aucune fiche de paie importée à supprimer.');
      return;
    }
    setModalData({ payslips: target, year, month });
    openModal('payslip_delete');
  };

  const handlePayslipExportCSV = async () => {
    const t = localStorage.getItem('xsrfToken');
    const url = `${process.env.REACT_APP_API_URL}admin/exportPayslipsCSV?year=${year}&month=${month}`;
    try {
      const res = await fetch(url, {
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `export-payslips-${year}-${month}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Export impossible');
    }
  };

  // ── Invoice actions ───────────────────────────────────────────────
  // The whole month, BeSTT then Pennylane: a selection is retried with JBWEB-1267.
  const handleInvoiceImport = () => {
    setModalData({ period, monthLabel: monthLabel(period) });
    openModal('invoice_import');
  };

  return (
    <PageForm>
      <SubNavbar>
        <div className="flex justify-between items-center w-full gap-3">
          {showControls ? (
            <div className="flex-1 overflow-x-auto overflow-y-hidden scrollbar-hide">
              <div className="flex gap-3 items-center min-w-max">
                <div className="flex items-center gap-1 text-sm">
                  <button
                    className="flex items-center justify-center w-9 h-9 rounded-md border hover:bg-gray-100"
                    onClick={() =>
                      setSelectedDate(
                        (prev) =>
                          new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                      )
                    }
                  >
                    <ChevronLeft wh={16} color="#374151" />
                  </button>
                  <DatePicker
                    selected={displayedDate}
                    onChange={(date) => setSelectedDate(date)}
                    maxDate={isInvoiceTab ? lastInvoiceDay : undefined}
                    showMonthYearPicker
                    dateFormat="yyyy/MM"
                    className="w-full min-w-[110px] h-10 ft-b border rounded-md text-center text-sm max-h-[38px] focus:border-yellow-500 focus:outline-none"
                    locale="fr-FR"
                  />
                  <button
                    className="flex items-center justify-center w-9 h-9 rounded-md border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[transparent]"
                    disabled={isAtLastInvoiceMonth}
                    onClick={() =>
                      setSelectedDate(
                        (prev) =>
                          new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                      )
                    }
                  >
                    <ChevronRight wh={16} color="#374151" />
                  </button>
                </div>
                {isInvoiceTab && <InvoiceFilters {...invoiceFilters.controls} />}
                {isPayslipTab && (
                  <>
                    <div className="flex-1 max-w-[260px] text-sm">
                      <StyledInput
                        type="text"
                        placeholder="Rechercher"
                        imgFront={<SearchLoop wh={16} color="#6B7280" />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <div className="max-w-[160px]">
                      <select
                        className="w-full h-10 pl-2 pr-3 ft-b placeholder-gray-600 border rounded-md text-sm focus:border-yellow-500 focus:outline-none"
                        onChange={(e) => setStatusFilter(e.target.value)}
                        value={statusFilter}
                      >
                        <option value="">Tous</option>
                        <option value="imported">Importé</option>
                        <option value="not_imported">Non importé</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : isRemindersTab ? (
            <div className="flex-1 overflow-x-auto overflow-y-hidden scrollbar-hide">
              <div className="flex gap-3 items-center min-w-max">
                <RemindersSearch {...reminderFilters.controls} />
              </div>
            </div>
          ) : (
            <div className="flex-1" />
          )}

          <div className="flex-shrink-0 flex items-center gap-3">
            {showControls && (
              <>
                {isPayslipTab && (
                  <>
                    <div className="border rounded-md py-2 px-3 text-sm font-medium bg-gray-50 whitespace-nowrap">
                      Total : {isWaiting ? <span className="inline-block w-16 h-4 bg-gray-200 rounded animate-pulse align-middle" /> : `${totalAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
                    </div>
                    <div className="relative" ref={multiDropdownRef}>
                      <Button
                        onClick={() => {
                          if (!multiDropdownOpen) updateMultiDropdownPos();
                          setMultiDropdownOpen(!multiDropdownOpen);
                        }}
                        color="PRIMARY"
                      >
                        <div className="flex gap-1 items-center">
                          <span>Actions{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}</span>
                          <ChevronDown wh={20} color="#FFFFFF" />
                        </div>
                      </Button>
                      {multiDropdownOpen && createPortal(
                        <div
                          ref={multiDropdownMenuRef}
                          className="fixed border-2 border-[#F3F4F6] rounded-lg shadow-lg w-56 bg-gray-50 z-[9999]"
                          style={{ top: multiDropdownPos.top, left: multiDropdownPos.left }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <div
                            className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                            onClick={handlePayslipImport}
                          >
                            <Import wh={16} color="#374151" />
                            Importer
                          </div>
                          <div className="border-b border-gray-100" />
                          <div
                            className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-red-500"
                            onClick={handlePayslipDelete}
                          >
                            Supprimer
                          </div>
                        </div>,
                        document.body,
                      )}
                    </div>
                  </>
                )}
                {isInvoiceTab && <InvoiceHeaderMenu onOpenJournal={openJournalFromMenu} />}
                {isInvoiceTab && (
                  <Button color="PRIMARY" onClick={handleInvoiceImport}>
                    <div className="flex gap-1 items-center">
                      <Plus wh={20} color="#FFFFFF" />
                      <span>{invoiceListText('importButton', 'Importer')}</span>
                    </div>
                  </Button>
                )}
                {/* Invoices are exported from their selection bar (JBWEB-1273). */}
                {isPayslipTab && (
                  <Button size="SMI" color="SECONDARY" onClick={handlePayslipExportCSV}>
                    <div className="flex gap-1 items-center text-sm">Export CSV</div>
                  </Button>
                )}
              </>
            )}
            {isRemindersTab && userData?.accessInvoice && (
              <Button
                color={reminderRun.isRunning ? 'DISABLED' : 'PRIMARY'}
                disabled={reminderRun.isRunning}
                onClick={openRemindAll}
              >
                {reminderRun.isRunning
                  ? remindersText('remindAllRunning')
                  : remindersText('remindAll')}
              </Button>
            )}
            {isBilling && (
              <>
                <div className="h-8 w-px bg-gray-200" />
                <BillingSubNav remindersToDo={remindersSummary.summary?.toDo} />
              </>
            )}
          </div>
        </div>
      </SubNavbar>

      {/* flex-1, not h-full: under the SubNavbar a full height overflows the
          page by the bar's height, and the page scrolls on top of the list. */}
      <div className="p-3 flex flex-col w-full flex-1 min-h-0 gap-3">
        <Outlet
          context={{
            // shared
            currentPage,
            setCurrentPage,
            totalPages,
            countPerPage,
            countTotal,
            isWaiting,
            selectedIds,
            setSelectedIds,
            year,
            month,
            // payslip-specific
            payslipsToDisplay: isPayslipTab ? itemsToDisplay : [],
            filteredPayslips,
            payslips,
            fetchPayslips,
            // invoice-specific
            // Rendered by the tab, inside its scroll: the banner goes up with
            // the list rather than taking the top of the page for good.
            invoiceImportBanner: isInvoiceTab && (
              <InvoiceImportErrorBanner
                unresolvedErrors={importState?.unresolvedErrors}
                lastRun={importState?.lastRun}
                onRetryAll={() => retryInvoiceImports()}
                onShowJournal={openJournal}
                isRetrying={isImportRunning}
              />
            ),
            monthInvoices,
            period,
            onImportInvoices: handleInvoiceImport,
            onClearInvoiceFilters: invoiceFilters.clear,
            invoiceDisplay,
            invoiceSelection,
            onRetryInvoices: retryInvoiceImports,
            isInvoiceImportRunning: isImportRunning,
            // reminders-specific (JBWEB-1283)
            remindersSummary,
            reminderFilters: reminderFilters.filters,
            onClearReminderFilters: reminderFilters.clear,
            remindersRefreshKey,
          }}
        />
      </div>
      {trackedRunId && !isImportToastHidden && (
        <InvoiceImportToast
          run={importRun}
          onClose={() => setIsImportToastHidden(true)}
          onShowErrors={isInvoiceTab ? openJournal : undefined}
        />
      )}
      {isInvoiceTab && journalTab && (
        <InvoiceImportJournal
          key={journalOpenings}
          period={period}
          initialTab={journalTab}
          refreshKey={journalRefreshKey}
          onClose={() => setJournalTab(null)}
          onRetry={(invoiceNumbers) => retryInvoiceImports(invoiceNumbers, { fromJournal: true })}
          isRetryDisabled={isImportRunning}
        />
      )}
    </PageForm>
  );
};

export default PayslipManagement;
